import { CSSObject } from '@emotion/react';
import { Frame } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DEFAULT_LENGTH } from '../../content-composer/constants';

const ALIGNMENT_STYLES = {
  left: { justifyContent: 'flex-start', textAlign: 'left' } as const,
  right: { justifyContent: 'flex-end', textAlign: 'right' } as const,
  center: { justifyContent: 'center', textAlign: 'center' } as const,
} as const;

const isValidAlignment = (alignment?: string): alignment is keyof typeof ALIGNMENT_STYLES => {
  return Boolean(alignment && alignment in ALIGNMENT_STYLES);
};

const isValidLengthValue = (value?: string): value is string => {
  return Boolean(value && value.trim().length > 0);
};

/**
 * Converts frame alignment to CSS
 */
const getAlignmentStyle = (frame?: Frame): CSSObject => {
  const alignment = frame?.alignment;
  return isValidAlignment(alignment) ? ALIGNMENT_STYLES[alignment] : ALIGNMENT_STYLES.center;
};

/**
 * Converts frame background color to CSS
 */
const getBackgroundColorStyle = (frame?: Frame): CSSObject => {
  const backgroundColor = frame?.backgroundColor?.trim();
  return backgroundColor ? { backgroundColor } : {};
};

/**
 * Normalizes a CSS length value to include a 'px' unit if only numbers are provided
 * @param value - The length value (e.g., "24" or "24px" or "1rem")
 * @returns Normalized padding value with appropriate unit
 */
const normalizeLengthValue = (value?: string): string => {
  if (!isValidLengthValue(value)) return DEFAULT_LENGTH;

  const trimmedValue = value.trim();
  const isNumericOnly = /^\d+(\.\d+)?$/.test(trimmedValue);

  return isNumericOnly ? `${trimmedValue}px` : trimmedValue;
};

/**
 * Converts frame padding values to CSS
 */
const getPaddingStyle = (frame?: Frame): CSSObject => {
  if (!frame) return { padding: DEFAULT_LENGTH };

  const { paddingTop, paddingRight, paddingBottom, paddingLeft } = frame;

  const top = normalizeLengthValue(paddingTop);
  const right = normalizeLengthValue(paddingRight);
  const bottom = normalizeLengthValue(paddingBottom);
  const left = normalizeLengthValue(paddingLeft);

  // Use padding shorthand notation
  if (top === right && right === bottom && bottom === left) {
    return { padding: top };
  }

  if (top === bottom && left === right) {
    return { padding: `${top} ${right}` };
  }

  return { padding: `${top} ${right} ${bottom} ${left}` };
};

/**
 * Generates CSS styles for a Frame object
 * @param frame - The Frame object containing style properties
 * @returns CSSObject with frame styles applied
 */
export const getFrameStyle = (frame?: Frame): CSSObject => {
  return {
    ...getAlignmentStyle(frame),
    ...getBackgroundColorStyle(frame),
    ...getPaddingStyle(frame),
  };
};
